import type { FormEventHandler, Ref } from "react";
import { Mic, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export type CoachVeeraDictationStatus = "idle" | "recording" | "transcribing" | "error";

export interface CoachVeeraComposerDictation {
  supported: boolean;
  status: CoachVeeraDictationStatus;
  error: string | null;
  start: () => void | Promise<void>;
  stop: () => void | Promise<void>;
}

interface CoachVeeraComposerProps {
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
  placeholder?: string;
  disabled?: boolean;
  submitDisabled?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  className?: string;
  controlsClassName?: string;
  inputClassName?: string;
  micButtonClassName?: string;
  sendButtonClassName?: string;
  dictation: CoachVeeraComposerDictation;
}

function getDictationLabel(status: CoachVeeraDictationStatus) {
  return status === "recording" ? "Stop voice dictation" : "Start voice dictation";
}

function getInlineStatus(status: CoachVeeraDictationStatus, error: string | null) {
  if (status === "transcribing") return "Transcribing your note...";
  if (status === "error" && error) return error;
  return null;
}

export function CoachVeeraComposer({
  value,
  onValueChange,
  onSubmit,
  placeholder = "Message Coach Veera...",
  disabled = false,
  submitDisabled = false,
  inputRef,
  className,
  controlsClassName,
  inputClassName,
  micButtonClassName,
  sendButtonClassName,
  dictation,
}: CoachVeeraComposerProps) {
  const isBusy = disabled || dictation.status === "transcribing";
  const micLabel = getDictationLabel(dictation.status);
  const inlineStatus = getInlineStatus(dictation.status, dictation.error);

  return (
    <form onSubmit={onSubmit} className={cn("flex min-w-0 flex-col gap-2", className)}>
      <div className={cn("flex min-w-0 items-center gap-2", controlsClassName)}>
        <Input
          ref={inputRef}
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn("min-w-0 flex-1", inputClassName)}
        />
        <Button
          type="button"
          size="icon"
          variant="outline"
          aria-label={micLabel}
          disabled={!dictation.supported || isBusy}
          className={cn(
            "shrink-0 rounded-xl",
            dictation.status === "recording" && "border-primary bg-primary/10 text-primary hover:bg-primary/15",
            micButtonClassName,
          )}
          onClick={() => {
            if (dictation.status === "recording") {
              void dictation.stop();
              return;
            }
            void dictation.start();
          }}
        >
          <Mic className="h-4 w-4" />
        </Button>
        <Button
          type="submit"
          size="icon"
          aria-label="Send message"
          disabled={submitDisabled || disabled}
          className={cn("shrink-0 rounded-xl", sendButtonClassName)}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
      {inlineStatus ? (
        <p
          className={cn(
            "text-xs",
            dictation.status === "error" ? "text-destructive" : "text-muted-foreground",
          )}
          role={dictation.status === "error" ? "alert" : "status"}
        >
          {inlineStatus}
        </p>
      ) : null}
    </form>
  );
}
